// Library methods
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";

// Components
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import LanguageSetup from "./LanguageSetup";
import TimezoneSetup from "./TimezoneSetup";
import ReportLayoutSetup from "./ReportLayoutSetup";
import ReportOrderSetup from "./ReportOrderSetup";
import AdditionalLinks from "./AdditionalLinksSetup";
import { useTheme } from "@mui/material/styles";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import useLocalStorage from "src/hooks/storage/useLocalStorage";
import { useUpdateUserSettings } from "src/services/users/hooks/useUpdateUserSettings";

// Contexts
import ClinicSettingsProvider from "src/contexts/clinic/ClinicSettingsContext";

const LAST_SCREEN_INDEX = 4;

const WelcomeModal = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [currentScreen, setCurrentScreen] = useState(0);

  const { mutate: mutateUserSettings } = useUpdateUserSettings();

  const [displayWelcomeModal, setDisplayWelcomeModal] = useLocalStorage(
    "displayWelcomeModal",
    false
  );

  const onClose = useCallback(() => {
    mutateUserSettings({ displayWelcomeModal: false });
    setDisplayWelcomeModal(false);
  }, [mutateUserSettings, setDisplayWelcomeModal]);

  return (
    <DefaultModal
      isOpen={displayWelcomeModal}
      onClose={onClose}
      title={t("Welcome to Retinalogik!")}
      maxWidth="sm"
      hideHeaderButtons
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button
            sx={{
              ...ModalCustomCancel(theme),
              display: currentScreen === 0 ? "none" : "flex",
            }}
            onClick={() => {
              setCurrentScreen((prev) => prev - 1);
            }}
          >
            {t("Back")}
          </Button>
          <Button
            sx={ModalCustomConfirm}
            onClick={() => {
              if (currentScreen === LAST_SCREEN_INDEX) {
                onClose();
              } else {
                setCurrentScreen((prev) => prev + 1);
              }
            }}
          >
            {currentScreen === LAST_SCREEN_INDEX ? t("Confirm") : t("Next")}
          </Button>
        </Grid>
      }
    >
      <ClinicSettingsProvider>
        <Grid display="flex" flexDirection="column" p="16px" gap="20px">
          {currentScreen === 0 && <LanguageSetup />}
          {currentScreen === 1 && <TimezoneSetup />}
          {currentScreen === 2 && <ReportOrderSetup />}
          {currentScreen === 3 && <ReportLayoutSetup />}
          {currentScreen === LAST_SCREEN_INDEX && <AdditionalLinks />}
        </Grid>
      </ClinicSettingsProvider>
    </DefaultModal>
  );
};

export default WelcomeModal;
